import { useCallback, useMemo, useState } from "react";

import useQuery from "../search/useQuery";
import QUERY_TAGS from "../search/queryTags";
import { invalidateTags } from "../search/queryCache";
import { cancelPayout, fetchPayout } from "./financeService";
import { mapPayout } from "./financeMappers";
import { VOLATILE_STALE_TIME } from "./financeCache";

const usePayoutDetail = ({ payoutId, enabled = true } = {}) => {
  const [cancelling, setCancelling] = useState(false);

  const query = useQuery({
    key: enabled && payoutId ? `finance:payout:${payoutId}` : null,
    tag: QUERY_TAGS.PAYOUT_DETAIL,
    staleTime: VOLATILE_STALE_TIME,
    enabled: enabled && Boolean(payoutId),
    fetcher: () => fetchPayout(payoutId),
  });

  const payout = useMemo(() => mapPayout(query.currentData), [query.currentData]);

  const cancel = useCallback(async () => {
    setCancelling(true);

    try {
      await cancelPayout(payoutId);
      invalidateTags([QUERY_TAGS.PAYOUT_LIST, QUERY_TAGS.PAYOUT_DETAIL, QUERY_TAGS.PAYMENT_WALLET]);
      await query.refetch();

      return { status: "cancelled" };
    } catch (error) {
      return { status: "error", error };
    } finally {
      setCancelling(false);
    }
  }, [payoutId, query]);

  return {
    payout,
    isLoading: !payout && !query.isError && query.enabled,
    isError: query.isError,
    error: query.error,
    cancelling,
    cancel,
    refetch: query.refetch,
  };
};

export default usePayoutDetail;
